import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './Orders.css';
import { ordersService, BookingOrder } from '../../services/ordersService';
import { useNavigate } from 'react-router-dom';

// Extended interface for additional properties not yet in API
interface ExtendedBookingOrder extends BookingOrder {
  refundInfo?: {
    amount: number;
    status: 'processed' | 'pending' | 'failed';
    refundId?: string;
    processedAt?: string;
  };
}

interface GroupedOrders {
  grouped: { [key: string]: ExtendedBookingOrder[] };
  individual: ExtendedBookingOrder[];
}

interface RefundDialogInfo {
  orderId: string;
  refund: any;
}

const Orders: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<ExtendedBookingOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled'>('all');
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
  const [cancellingGroupId, setCancellingGroupId] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState<string | null>(null);
  const [showGroupCancelDialog, setShowGroupCancelDialog] = useState<string | null>(null);
  const [refundCalculation, setRefundCalculation] = useState<any>(null);
  const [showRefundDialog, setShowRefundDialog] = useState<string | null>(null);
  const [calculatingRefund, setCalculatingRefund] = useState<boolean>(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState<string>('');
  const [rescheduleTime, setRescheduleTime] = useState<string>('');
  const [rescheduling, setRescheduling] = useState<boolean>(false);
  const [rescheduleError, setRescheduleError] = useState<string>('');
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);

  // Generate time slots similar to booking page
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 18; hour++) {
      const time24 = `${hour.toString().padStart(2, '0')}:00`;
      const time12 = hour <= 12 ? `${hour}:00 AM` : `${hour - 12}:00 PM`;
      if (hour === 12) {
        slots.push({ value: time24, label: '12:00 PM' });
      } else {
        slots.push({ value: time24, label: time12 });
      }
    }
    return slots;
  };  const [refundInfo, setRefundInfo] = useState<RefundDialogInfo | null>(null);
  const [expandedPricing, setExpandedPricing] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only re-run when user changes

  const loadOrders = async () => {
    try {
      setLoading(true);
      
      const userUuid = user?.userUuid;
      if (!userUuid) {
        console.log('No user UUID found, clearing orders');
        setOrders([]);
        return;
      }

      // Fetch orders from API
      const fetchedOrders = await ordersService.getUserOrders(userUuid);
      
      console.log('📦 Orders fetched from API:', fetchedOrders?.length || 0);
      if (fetchedOrders && fetchedOrders.length > 0) {
        console.log('📦 Orders with grouping:', fetchedOrders.map(order => ({
          id: order.id,
          orderId: order.orderId,
          serviceName: order.serviceName,
          serviceDate: order.serviceDate,
          groupBookingId: order.groupBookingId
        })));
      }
      
      // Always use API data (remove localStorage fallback)
      setOrders(fetchedOrders || []);
    } catch (error) {
      console.error('❌ Error loading orders:', error);
      // Set empty orders on API error instead of localStorage fallback
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const migrateLocalStorageOrders = (orders: ExtendedBookingOrder[]): ExtendedBookingOrder[] => {
    return orders.map((order) => {
      // Migrate old time format to new format
      let fixedServiceTime = order.serviceTime;
      if (order.serviceTime && !order.serviceTime.includes('AM') && !order.serviceTime.includes('PM')) {
        // Convert 24-hour format to 12-hour format if needed
        const [hours, minutes] = order.serviceTime.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12;
        fixedServiceTime = `${hour12}:${minutes} ${ampm}`;
      }

      // Ensure duration exists
      let fixedDuration = order.duration;
      if (!fixedDuration) {
        // Set default duration based on service type
        if (order.serviceName?.toLowerCase().includes('puja')) {
          fixedDuration = '2-3 hours';
        } else if (order.serviceName?.toLowerCase().includes('ritual')) {
          fixedDuration = '3-4 hours';
        } else {
          fixedDuration = '2 hours';
        }
      }

      // Migrate old address format
      let fixedAddress = order.address;
      if (typeof order.address === 'string') {
        // Convert old string address to new object format
        fixedAddress = {
          name: 'Home',
          line1: order.address as any,
          city: 'Unknown',
          state: 'Unknown',
          postalCode: '000000'
        };
      }

      // Ensure priest details exist
      let fixedPriestDetails = order.priestDetails;
      if (!fixedPriestDetails) {
        fixedPriestDetails = {
          name: 'Priest assigned',
          phone: '+91-XXXXXXXXXX',
          rating: 4.5,
          experience: '10+ years'
        };
      }

      // Add missing fields
      const fixedOrder = {
        ...order,
        id: order.id || `order_${Date.now()}`,
        orderId: order.orderId || `ORD${Date.now()}`,
        serviceTime: fixedServiceTime,
        duration: fixedDuration,
        address: fixedAddress,
        priestDetails: fixedPriestDetails,
        createdAt: order.createdAt || new Date().toISOString(),
        paymentStatus: order.paymentStatus || 'pending' as const,
        status: order.status || 'confirmed' as const
      };

      if (order.serviceTime !== fixedServiceTime || order.duration !== fixedDuration) {
        console.log(`🔧 Migrated order ${order.orderId}: ${order.serviceTime} → ${fixedServiceTime}, duration: ${fixedDuration}`);
      }

      return fixedOrder;
    });
  };

  const groupOrdersByBookingId = (orders: ExtendedBookingOrder[]): GroupedOrders => {
    const grouped: { [key: string]: ExtendedBookingOrder[] } = {};
    const individual: ExtendedBookingOrder[] = [];

    console.log('🔍 Grouping orders:', orders.length);
    
    // Helper function to sort orders by date (latest first)
    const sortOrdersByDate = (orderList: ExtendedBookingOrder[]) => {
      return orderList.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.serviceDate);
        const dateB = new Date(b.createdAt || b.serviceDate);
        return dateB.getTime() - dateA.getTime(); // Latest first
      });
    };
    
    // First pass: group all orders by groupBookingId
    const tempGroups: { [key: string]: ExtendedBookingOrder[] } = {};
    
    orders.forEach(order => {
      if (order.groupBookingId) {
        if (!tempGroups[order.groupBookingId]) {
          tempGroups[order.groupBookingId] = [];
        }
        tempGroups[order.groupBookingId].push(order);
      } else {
        // Orders without groupBookingId go directly to individual
        individual.push(order);
      }
    });

    // Second pass: only keep groups with 2+ orders, move singles to individual
    Object.entries(tempGroups).forEach(([groupId, groupOrders]) => {
      console.log(`🔍 Group ${groupId} has ${groupOrders.length} order(s)`);
      
      if (groupOrders.length >= 2) {
        // Sort orders within the group by date (latest first)
        grouped[groupId] = sortOrdersByDate([...groupOrders]);
        console.log(`✅ Added ${groupOrders.length} orders to group ${groupId}`);
      } else {
        // Single order with groupBookingId goes to individual
        individual.push(...groupOrders);
        console.log(`✅ Moved single order from group ${groupId} to individual orders`);
      }
    });

    // Sort individual orders by date (latest first) - already sorted from main sort, but ensure consistency
    const sortedIndividual = sortOrdersByDate([...individual]);

    console.log('🔍 Grouping results:', {
      groupedKeys: Object.keys(grouped),
      groupedCount: Object.keys(grouped).length,
      individualCount: sortedIndividual.length
    });

    return { grouped, individual: sortedIndividual };
  };

  const formatAmount = (amount: string | number | null | undefined) => {
    // Handle null, undefined, or invalid values
    if (amount === null || amount === undefined) {
      return '₹0';
    }
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    
    // Handle NaN or invalid numbers
    if (isNaN(numAmount)) {
      return '₹0';
    }
    
    return `₹${numAmount.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return '#2563eb';
      case 'in-progress': return '#f59e0b';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      case 'pending': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'failed': return '#ef4444';
      case 'refunded': return '#3b82f6';
      case 'not_required': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const togglePricingBreakdown = (orderId: string) => {
    setExpandedPricing(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const filterTabs = ['all', 'confirmed', 'in-progress', 'completed', 'cancelled'];

  // Sort orders by creation date/time (latest first) before filtering and grouping
  const sortedOrders = [...orders].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.serviceDate);
    const dateB = new Date(b.createdAt || b.serviceDate);
    return dateB.getTime() - dateA.getTime(); // Latest first
  });

  console.log('📅 Orders sorted by date (latest first):', sortedOrders.map(order => ({
    id: order.id,
    service: order.serviceName,
    date: order.createdAt || order.serviceDate,
    groupBookingId: order.groupBookingId
  })));

  const filteredOrders = sortedOrders.filter(order => 
    filter === 'all' || order.status === filter
  );

  const { grouped: groupedOrders, individual: individualOrders } = groupOrdersByBookingId(filteredOrders);

  const handleViewDetails = (order: ExtendedBookingOrder) => {
    console.log('👁️ Viewing order details:', order.id);
    // Navigate to order details page
  };

  const handleTrackOrder = (order: ExtendedBookingOrder) => {
    console.log('📍 Tracking order:', order.id, 'serviceRequestId:', order.serviceRequestId);
    navigate(`/track-order/${order.serviceRequestId}`, { state: { order } });
  };

  const handleCancelOrder = async (order: ExtendedBookingOrder) => {
    try {
      setCalculatingRefund(true);
      console.log('💰 Calculating refund for order:', order.id);
      
      // Calculate refund first
      const refundResult = await ordersService.calculateRefund(
        order.serviceRequestId, 
        'Customer requested cancellation'
      );
      
      if (refundResult.success && refundResult.data) {
        setRefundCalculation(refundResult.data);
        setShowRefundDialog(order.id);
      } else {
        console.error('❌ Failed to calculate refund:', refundResult.message);
        alert(refundResult.message || 'Failed to calculate refund. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error calculating refund:', error);
      alert('Failed to calculate refund. Please try again.');
    } finally {
      setCalculatingRefund(false);
    }
  };

  const handleCancelGroupBooking = (groupId: string) => {
    setShowGroupCancelDialog(groupId);
  };

  const confirmCancelOrder = async (order: ExtendedBookingOrder) => {
    try {
      setCancellingOrderId(order.id);
      setShowRefundDialog(null);
      setRefundCalculation(null);

      console.log('🚫 Cancelling order:', order.id);
      
      // Call the API to cancel the order
      const result = await ordersService.cancelServiceRequest(order.serviceRequestId);
      
      if (result.success) {
        console.log('✅ Order cancelled successfully:', result);
        
        // Show refund information using the calculated data
        if (refundCalculation) {
          setRefundInfo({ 
            orderId: order.id, 
            refund: {
              refundId: `REF-${Date.now()}`, // Generate a temporary refund ID
              amount: refundCalculation.refundAmount,
              status: refundCalculation.refundEligible ? 'PROCESSED' : 'NOT_ELIGIBLE',
              processedAt: refundCalculation.refundEligible ? new Date().toISOString() : null,
              totalAmount: refundCalculation.totalAmount,
              cancellationFee: refundCalculation.cancellationFee,
              refundStatus: refundCalculation.refundStatus
            }
          });
        }

        // Refresh orders
        await loadOrders();
      } else {
        console.error('❌ Failed to cancel order:', result.message);
        alert(result.message || 'Failed to cancel order');
      }
    } catch (error) {
      console.error('❌ Error cancelling order:', error);
      alert('Failed to cancel order. Please try again.');
    } finally {
      setCancellingOrderId(null);
    }
  };

  const confirmCancelGroupBooking = async (groupId: string) => {
    try {
      setCancellingGroupId(groupId);
      setShowGroupCancelDialog(null);

      console.log('🚫 Cancelling group booking:', groupId);
      
      // Call the API to cancel the group booking
      const result = await ordersService.cancelGroupBooking(groupId);
      
      if (result.success) {
        console.log('✅ Group booking cancelled successfully:', result);
        
        // Show refund information if available
        if (result.refundInfo) {
          setRefundInfo({ orderId: groupId, refund: result.refundInfo });
        }

        // Refresh orders
        await loadOrders();
      } else {
        console.error('❌ Failed to cancel group booking:', result.message);
        alert(result.message || 'Failed to cancel group booking');
      }
    } catch (error) {
      console.error('❌ Error cancelling group booking:', error);
      alert('Failed to cancel group booking. Please try again.');
    } finally {
      setCancellingGroupId(null);
    }
  };

  const handleMakePayment = (order: ExtendedBookingOrder) => {
    console.log('💳 Making payment for order:', {
      orderId: order.id,
      paymentStatus: order.paymentStatus,
      paymentMode: order.paymentMode,
      totalAmount: order.totalAmount
    });

    // Prepare payment navigation state based on order type
    const paymentState: any = {
      // Order identification
      orderId: order.id,
      bookingId: order.serviceRequestId || order.id, // Use serviceRequestId as bookingId
      serviceRequestUuid: order.serviceRequestId, // serviceRequestId contains the UUID
      
      // Service details
      serviceName: order.serviceName,
      totalAmount: order.totalAmount,
      baseAmount: order.baseAmount,
      gstAmount: order.gstAmount,
      
      // Booking details
      selectedDate: order.serviceDate,
      selectedTime: order.serviceTime,
      selectedAddress: order.address,
      duration: order.duration || '60',
      
      // Payment context
      isRetryPayment: order.paymentStatus === 'failed',
      isPostpaidPayment: order.paymentMode === 'POSTPAID',
      isBookingPayment: true,
      fromOrdersPage: true, // Explicit flag to indicate coming from orders
      
      // Group booking handling
      ...(order.groupBookingId && {
        isGroupBooking: true,
        groupBookingId: order.groupBookingId
      })
    };

    console.log('🚀 Navigating to payment with state:', paymentState);
    navigate('/payment', { state: paymentState });
  };

  const handleMakeGroupPayment = (groupOrders: ExtendedBookingOrder[]) => {
    setProcessingPayment(true);
    
    console.log('💳 Making payment for group booking:', {
      groupId: groupOrders[0].groupBookingId,
      orderCount: groupOrders.length,
      orders: groupOrders.map(order => ({
        orderId: order.id,
        serviceName: order.serviceName,
        totalAmount: order.totalAmount
      }))
    });

    try {
      // Calculate total amount for all services in the group
      const totalAmount = groupOrders.reduce((sum, order) => {
        const amount = typeof order.totalAmount === 'string' ? parseFloat(order.totalAmount) : order.totalAmount;
        const validAmount = (amount && !isNaN(amount)) ? amount : 0;
        return sum + validAmount;
      }, 0);

      // Use the first order as the base template for group payment
      const baseOrder = groupOrders[0];
      
      // Prepare group payment navigation state
      const paymentState: any = {
        // Group identification
        groupBookingId: baseOrder.groupBookingId,
        orderId: `GROUP-${baseOrder.groupBookingId}`,
        
        // Service details (show as group)
        serviceName: `Group Booking (${groupOrders.length} services)`,
        totalAmount: totalAmount,
        baseAmount: totalAmount / 1.18, // Approximate base amount
        gstAmount: totalAmount - (totalAmount / 1.18),
        
        // Booking details from first service
        selectedDate: baseOrder.serviceDate,
        selectedTime: baseOrder.serviceTime,
        selectedAddress: baseOrder.address,
        duration: baseOrder.duration || '60',
        
        // Payment context
        isRetryPayment: groupOrders.some(order => order.paymentStatus === 'failed'),
        isPostpaidPayment: groupOrders.some(order => order.paymentMode === 'POSTPAID'),
        isBookingPayment: true,
        fromOrdersPage: true, // Explicit flag to indicate coming from orders
        
        // Group booking flags
        isGroupBooking: true,
        
        // Additional group info
        groupServices: groupOrders.map(order => ({
          serviceName: order.serviceName,
          amount: order.totalAmount,
          serviceDate: order.serviceDate,
          serviceTime: order.serviceTime
        }))
      };

      console.log('🚀 Navigating to group payment with state:', paymentState);
      navigate('/payment', { state: paymentState });
    } catch (error) {
      console.error('❌ Error preparing group payment:', error);
      alert('Failed to prepare group payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const shouldShowGroupPaymentButton = (groupOrders: ExtendedBookingOrder[]): boolean => {
    // Show group payment button if any order in the group needs payment
    return groupOrders.some(order => 
      order.status !== 'cancelled' && 
      order.status !== 'completed' && 
      (order.paymentStatus === 'failed' ||
       order.paymentStatus === 'pending' ||
       (order.paymentMode === 'POSTPAID' && order.paymentStatus === 'not_required'))
    );
  };

  const shouldShowPaymentButton = (order: ExtendedBookingOrder, isInGroupWithMultipleServices: boolean = false): boolean => {
    // Don't show individual payment buttons if this order is part of a group with multiple services
    if (isInGroupWithMultipleServices) {
      return false;
    }
    
    // Show payment button for:
    // 1. Failed payments that need retry
    // 2. Postpaid orders that haven't been paid yet
    // 3. Pending payments
    // 4. Orders that are not cancelled or completed
    
    if (order.status === 'cancelled' || order.status === 'completed') {
      return false;
    }

    return (
      order.paymentStatus === 'failed' ||
      order.paymentStatus === 'pending' ||
      (order.paymentMode === 'POSTPAID' && 
       order.paymentStatus === 'not_required')
    );
  };

  const getPaymentButtonText = (order: ExtendedBookingOrder): string => {
    if (order.paymentStatus === 'failed') {
      return 'Retry Payment';
    }
    if (order.paymentMode === 'POSTPAID') {
      return 'Pay Now';
    }
    return 'Complete Payment';
  };

  const cancelCancelOrder = () => {
    setShowCancelDialog(null);
    setShowRefundDialog(null);
    setRefundCalculation(null);
  };

  const cancelGroupCancelOrder = () => {
    setShowGroupCancelDialog(null);
  };

  const handleRescheduleOrder = (order: ExtendedBookingOrder) => {
    setShowRescheduleDialog(order.id);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleError('');
  };

  const cancelRescheduleOrder = () => {
    setShowRescheduleDialog(null);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleError('');
  };

  const confirmRescheduleOrder = async (order: ExtendedBookingOrder) => {
    if (!rescheduleDate || !rescheduleTime) {
      setRescheduleError('Please select both date and time');
      return;
    }

    try {
      setRescheduling(true);
      setRescheduleError('');

      // Combine date and time
      const newDateTime = new Date(`${rescheduleDate}T${rescheduleTime}`);
      const originalDateTime = new Date(order.serviceDate);
      
      // Check 72-hour rule
      const timeDiff = newDateTime.getTime() - originalDateTime.getTime();
      const hoursDiff = Math.abs(timeDiff) / (1000 * 60 * 60);
      
      if (hoursDiff < 72) {
        setRescheduleError('Rescheduling is only allowed 72 hours before the original service date. Please contact customer support for assistance.');
        return;
      }

      // Calculate slot end time (assuming 2 hours duration)
      const newSlotEnd = new Date(newDateTime.getTime() + (2 * 60 * 60 * 1000));

      const result = await ordersService.rescheduleServiceRequest(
        order.serviceRequestId,
        newDateTime.toISOString(),
        newSlotEnd.toISOString(),
        'Customer requested different time'
      );

      if (result.success) {
        console.log('✅ Service rescheduled successfully:', result);
        setShowRescheduleDialog(null);
        
        // Show success message with readable time
        const selectedSlot = generateTimeSlots().find(slot => slot.value === rescheduleTime);
        const timeDisplay = selectedSlot ? selectedSlot.label : rescheduleTime;
        alert(`Service rescheduled successfully! New date: ${rescheduleDate} at ${timeDisplay}`);
        
        // Refresh orders
        await loadOrders();
      } else {
        setRescheduleError(result.message);
      }
    } catch (error) {
      console.error('❌ Error rescheduling service:', error);
      setRescheduleError('Failed to reschedule service. Please try again.');
    } finally {
      setRescheduling(false);
    }
  };

  // Function to render individual order card content
  const renderOrderCard = (order: ExtendedBookingOrder, index: number, isGroupItem: boolean = false, groupOrders: ExtendedBookingOrder[] = []) => (
    <>
      <div className="order-header">
        <div className="order-info">
          <div className="order-id">
            <span className="label">Order ID:</span>
            <span className="value">{order.orderId}</span>
          </div>
          {!isGroupItem && (
            <div className="order-date">
              <span className="label">Booked on:</span>
              <span className="value">{formatDateTime(order.createdAt)}</span>
            </div>
          )}
        </div>
        <div className="order-status">
          <span 
            className="status-badge"
            style={{ backgroundColor: getStatusColor(order.status) }}
          >
            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          </span>
        </div>
      </div>

      <div className="order-content">
        <div className="service-info">
          <div className="service-image">
            <img 
              src={order.serviceImage || '/images/carousel/default-service.jpg'} 
              alt={order.serviceName}
            />
          </div>
          <div className="service-details">
            <h3>{order.serviceName}</h3>
            {order.planName && <p className="plan-name">{order.planName}</p>}
            <div className="service-schedule">
              <div className="schedule-item">
                <span className="icon">📅</span>
                <span>Date: {formatDate(order.serviceDate)}</span>
              </div>
              <div className="schedule-item">
                <span className="icon">🕐</span>
                <span>Time: {order.serviceTime}</span>
              </div>
              {order.duration && (
                <div className="schedule-item">
                  <span className="icon">⏱️</span>
                  <span>Duration: {order.duration}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="order-amount">
          <div className="amount-display">
            <span className="amount">{formatAmount(order.totalAmount)}</span>
            <span 
              className="payment-status"
              style={{ color: getPaymentStatusColor(order.paymentStatus) }}
            >
              {order.paymentStatus === 'not_required' ? 'Pay Later' : 
               order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1)}
            </span>
          </div>

          {/* Pricing Breakdown */}
          <div className="pricing-breakdown">
            <div 
              className="pricing-summary" 
              onClick={() => togglePricingBreakdown(order.id)}
              style={{ cursor: 'pointer' }}
            >
              <span className="breakdown-toggle">
                💰 {expandedPricing.has(order.id) ? 'Hide' : 'View'} Pricing Details
              </span>
              <span className="toggle-arrow">
                {expandedPricing.has(order.id) ? '▼' : '▶'}
              </span>
            </div>
            {expandedPricing.has(order.id) && (
              <div className="pricing-details">
                {order.baseAmount && (
                  <div className="price-row">
                    <span className="price-label">Base Amount:</span>
                    <span className="price-value">{formatAmount(order.baseAmount)}</span>
                  </div>
                )}
                {order.gstAmount && order.gstPercentage && (
                  <div className="price-row">
                    <span className="price-label">GST ({order.gstPercentage}%):</span>
                    <span className="price-value">{formatAmount(order.gstAmount)}</span>
                  </div>
                )}
                {order.convenienceFee && (
                  <div className="price-row">
                    <span className="price-label">Convenience Fee:</span>
                    <span className="price-value">{formatAmount(order.convenienceFee)}</span>
                  </div>
                )}
                <div className="price-row total-row">
                  <span className="price-label">Total Amount:</span>
                  <span className="price-value">{formatAmount(order.totalAmount)}</span>
                </div>
                
                {/* Cancellation and Refund Details */}
                {order.status === 'cancelled' && (
                  <div className="cancellation-details">
                    {order.cancellationFee && (
                      <div className="price-row cancellation-row">
                        <span className="price-label">Cancellation Fee:</span>
                        <span className="price-value negative">{formatAmount(order.cancellationFee)}</span>
                      </div>
                    )}
                    {order.refundAmount && (
                      <div className="price-row refund-row">
                        <span className="price-label">Refund Amount:</span>
                        <span className="price-value positive">{formatAmount(order.refundAmount)}</span>
                      </div>
                    )}
                    {order.cancelledAt && (
                      <div className="cancellation-info">
                        <span className="cancellation-date">Cancelled on: {formatDateTime(order.cancelledAt)}</span>
                        {order.cancellationReason && (
                          <span className="cancellation-reason">Reason: {order.cancellationReason}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {(order.paymentStatus === 'pending' || order.paymentStatus === 'failed' || 
            (order.paymentMode === 'POSTPAID' && order.paymentStatus === 'not_required')) && 
            order.paymentDueDate && (
            <div className={`payment-due ${
              order.paymentStatus === 'failed' ? 'failed' : 
              new Date(order.paymentDueDate) < new Date() ? 'overdue' : 'pending'
            }`}>
              <span className="due-label">
                {order.paymentStatus === 'failed' ? 'Payment Failed - Retry Before:' :
                 order.paymentMode === 'POSTPAID' ? 'Pay Before Service:' :
                 'Payment Due:'}
              </span>
              <span className="due-date">{formatDateTime(order.paymentDueDate)}</span>
              {new Date(order.paymentDueDate) < new Date() && (
                <span className="overdue-warning">⚠️ Overdue</span>
              )}
            </div>
          )}
          
          {order.refundInfo && (
            <div className="refund-info">
              <span className="refund-label">Refund:</span>
              <span className="refund-amount">₹{order.refundInfo.amount}</span>
              <span className={`refund-status ${order.refundInfo.status.toLowerCase()}`}>
                {order.refundInfo.status}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="order-address">
        <div className="address-header">
          <span className="icon">📍</span>
          <span className="label">Service Address</span>
        </div>
        <div className="address-details">
          <strong>{order.address.name || order.address.label}</strong>
          <br />
          {order.address.line1}
          {order.address.line2 && <>, {order.address.line2}</>}
          <br />
          {order.address.city}, {order.address.state} - {order.address.postalCode}
          {/* Note: landmark not available in API response yet */}
        </div>

        {/* Hide Assigned Acharya section if service is cancelled and priest assignment is pending */}
        {!(order.status === 'cancelled' && (!order.assignedPriests || order.assignedPriests.length === 0) && !order.priestDetails) && (
        <div className="priest-details">
          <div className="priest-header">
            {/* <span className="icon">🧙‍♂️</span> */}
            <span className="label">Assigned Acharya{order.assignedPriests && order.assignedPriests.length > 1 ? 's' : ''}</span>
          </div>
          <div className="priest-info">
            {order.assignedPriests && order.assignedPriests.length > 0 ? (
              <div className="multiple-priests">
                {order.assignedPriests.map((priest, index) => (
                  <div key={index} className="priest-item">
                    <div className="priest-name-rating">
                      <div className="priest-name-info">
                        <strong>{priest.name}</strong>
                        <div className="priest-rating">
                          <span className="rating-stars">⭐</span>
                          <span>{priest.rating}</span>
                        </div>
                      </div>
                      <span className={`priest-status ${priest.status.toLowerCase()}`}>
                        {priest.status === 'ACCEPTED' ? '✅' : '⏳'} {priest.status}
                      </span>
                    </div>
                    <div className="priest-details-row">
                      <span>🎓 {priest.experience}</span>
                    </div>
                    {priest.acceptedAt && (
                      <div className="priest-timing">
                        <small>Accepted: {new Date(priest.acceptedAt).toLocaleDateString()}</small>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : order.priestDetails ? (
              <>
                <div className="priest-name-rating">
                  <strong>{order.priestDetails.name}</strong>
                  <div className="priest-rating">
                    <span className="rating-stars">⭐</span>
                    <span>{order.priestDetails.rating}</span>
                  </div>
                </div>
                <div className="priest-details-row">
                  <span>📞 {order.priestDetails.phone}</span>
                  <span>🎓 {order.priestDetails.experience}</span>
                </div>
              </>
            ) : (
              <div className="priest-pending">
                <span>Priest assignment pending...</span>
                {order.maxProviders && order.maxProviders > 1 && (
                  <small>({order.maxProviders} priests required)</small>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      <div className="order-actions">
        {order.status !== 'cancelled' && (
          <button
            className="action-button primary"
            onClick={() => handleTrackOrder(order)}
          >
            Track Order
          </button>
        )}
        {shouldShowPaymentButton(order, isGroupItem && groupOrders.length > 1) && (
          <button
            className={`action-button payment ${
              order.paymentStatus === 'failed' ? 'retry' : 'primary'
            }`}
            onClick={() => handleMakePayment(order)}
          >
            {getPaymentButtonText(order)}
          </button>
        )}
        {order.status !== 'cancelled' && order.status !== 'completed' && (
          <button
            className="action-button reschedule"
            onClick={() => handleRescheduleOrder(order)}
            disabled={rescheduling}
            title="Reschedule this service"
          >
            {rescheduling ? 'Rescheduling...' : 'Reschedule'}
          </button>
        )}
        {order.status !== 'cancelled' && order.status !== 'completed' && !order.groupBookingId && (
          <button
            className="action-button cancel"
            onClick={() => handleCancelOrder(order)}
            disabled={cancellingOrderId === order.id || calculatingRefund}
          >
            {cancellingOrderId === order.id ? 'Cancelling...' : 
             calculatingRefund ? 'Calculating...' : 'Cancel Order'}
          </button>
        )}
        {order.groupBookingId && order.status !== 'cancelled' && order.status !== 'completed' && (
          <button
            className="action-button cancel individual"
            onClick={() => handleCancelOrder(order)}
            disabled={cancellingOrderId === order.id || calculatingRefund}
            title="Cancel only this service from the group booking"
          >
            {cancellingOrderId === order.id ? 'Cancelling...' : 
             calculatingRefund ? 'Calculating...' : 'Cancel Service'}
          </button>
        )}
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="orders-page">
        <div className="orders-container">
          <div className="loading-text">
            <p>Loading<span className="loading-dots"></span></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-container">
        <div className="orders-header">
          <h1>My Orders</h1>
          <p>Track and manage your service bookings</p>
        </div>

        <div className="filter-tabs">
          {filterTabs.map((status, index) => (
            <button
              key={`filter-${status}-${index}`}
              className={`filter-tab ${filter === status ? 'active' : ''}`}
              onClick={() => setFilter(status as any)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              <span className="count">
                ({status === 'all' ? orders.length : orders.filter(order => order.status === status).length})
              </span>
            </button>
          ))}
        </div>

        <div className="orders-list">
          {filteredOrders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No orders found</h3>
              <p>
                {filter === 'all' 
                  ? "You haven't made any bookings yet. Start by booking a service!" 
                  : `No ${filter} orders found.`}
              </p>
              <button 
                className="cta-button"
                onClick={() => navigate('/')}
              >
                Browse Services
              </button>
            </div>
          ) : (
            <>
              {/* Group Bookings Section */}
              {Object.keys(groupedOrders).length > 0 && (
                <div className="section-header">
                  <h3>Group Bookings</h3>
                  <p>Multiple services booked together</p>
                </div>
              )}
              {Object.entries(groupedOrders)
                .sort(([,groupOrdersA], [,groupOrdersB]) => {
                  // Sort groups by the latest order within each group
                  const latestDateA = new Date(groupOrdersA[0].createdAt || groupOrdersA[0].serviceDate);
                  const latestDateB = new Date(groupOrdersB[0].createdAt || groupOrdersB[0].serviceDate);
                  return latestDateB.getTime() - latestDateA.getTime();
                })
                .map(([groupId, groupOrders]) => (
                <div key={`group-${groupId}`} className="group-booking-card">
                  <div className="group-header">
                    <div className="group-info">
                      <h3>🛒 Group Booking ({groupOrders.length} services)</h3>
                      <span className="group-id">Group ID: {groupId}</span>
                      <span className="group-date">
                        Booked on: {formatDateTime(groupOrders[0].createdAt)}
                      </span>
                    </div>
                    <div className="group-actions">
                      <div className="group-total">
                        <span className="total-amount">
                          Total: {formatAmount(groupOrders.reduce((sum, order) => {
                            const amount = typeof order.totalAmount === 'string' ? parseFloat(order.totalAmount) : order.totalAmount;
                            // Handle null/undefined/NaN amounts
                            const validAmount = (amount && !isNaN(amount)) ? amount : 0;
                            return sum + validAmount;
                          }, 0))}
                        </span>
                      </div>
                      <div className="group-buttons">
                        {shouldShowGroupPaymentButton(groupOrders) && (
                          <button
                            className="action-button payment group-payment"
                            onClick={() => handleMakeGroupPayment(groupOrders)}
                            disabled={processingPayment}
                          >
                            {processingPayment ? 'Processing...' : 'Pay Now'}
                          </button>
                        )}
                        {groupOrders.every(order => order.status !== 'cancelled' && order.status !== 'completed') && (
                          <button
                            className="action-button cancel group-cancel"
                            onClick={() => handleCancelGroupBooking(groupId)}
                            disabled={cancellingGroupId === groupId}
                          >
                            {cancellingGroupId === groupId ? 'Cancelling...' : 'Cancel All'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="group-services">
                    {groupOrders.map((order, index) => (
                      <div key={`${order.id}-${index}`} className="order-card group-item">
                        {renderOrderCard(order, index, true, groupOrders)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Individual Orders Section */}
              {individualOrders.length > 0 && (
                <div className="section-header">
                  <h3>Individual Bookings</h3>
                  <p>Single service requests</p>
                </div>
              )}
              {individualOrders.map((order, index) => (
                <div key={`${order.id}-${index}`} className="order-card">
                  {renderOrderCard(order, index, false, [])}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Refund Calculation Dialog */}
      {showRefundDialog && refundCalculation && (
        <div className="cancel-dialog-overlay">
          <div className="cancel-dialog refund-dialog">
            <div className="dialog-header">
              <h3>Cancellation & Refund Details</h3>
            </div>
            <div className="dialog-content">
              <div className="refund-summary">
                {(() => {
                  const order = orders.find(o => o.id === showRefundDialog);
                  return order ? (
                    <div className="service-info">
                      <h4>{order.serviceName}</h4>
                      <p>Order ID: <strong>{order.orderId}</strong></p>
                      <p>Service Date: <strong>{order.serviceDate}</strong></p>
                    </div>
                  ) : null;
                })()}
                
                <div className="refund-breakdown">
                  <div className="refund-row">
                    <span>Total Amount Paid:</span>
                    <span className="amount">₹{refundCalculation.totalAmount?.toLocaleString()}</span>
                  </div>
                  
                  {refundCalculation.cancellationFee > 0 && (
                    <div className="refund-row fee">
                      <span>Cancellation Fee:</span>
                      <span className="amount fee">₹{refundCalculation.cancellationFee?.toLocaleString()}</span>
                    </div>
                  )}
                  
                  <div className="refund-row total">
                    <span><strong>Refund Amount:</strong></span>
                    <span className="amount total">₹{refundCalculation.refundAmount?.toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="policy-info">
                  <h5>Cancellation Policy Applied:</h5>
                  <div className="policy-details">
                    {/* <p><strong>{refundCalculation.appliedPolicy?.policyName}</strong></p> */}
                    <p>{refundCalculation.appliedPolicy?.description}</p>
                    <p className="reason">{refundCalculation.reason}</p>
                  </div>
                </div>
                
                {!refundCalculation.refundEligible && (
                  <div className="warning-text">
                    <strong>⚠️ No Refund Available:</strong> This booking is not eligible for a refund based on our cancellation policy.
                  </div>
                )}
                
                {refundCalculation.refundEligible && refundCalculation.refundAmount > 0 && (
                  <div className="success-text">
                    <strong>✅ Refund Eligible:</strong> The refund amount will be processed back to your original payment method within 5-7 business days.
                  </div>
                )}
              </div>
            </div>
            <div className="dialog-actions">
              <button 
                className="dialog-button secondary"
                onClick={cancelCancelOrder}
              >
                Keep Order
              </button>
              <button 
                className="dialog-button danger"
                onClick={() => {
                  const order = orders.find(o => o.id === showRefundDialog);
                  if (order) confirmCancelOrder(order);
                }}
                disabled={calculatingRefund}
              >
                {calculatingRefund ? 'Processing...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Dialog */}
      {showRescheduleDialog && (
        <div className="cancel-dialog-overlay">
          <div className="cancel-dialog reschedule-dialog">
            <div className="dialog-header">
              <h3>Reschedule Service</h3>
            </div>
            <div className="dialog-content">
              <div className="reschedule-form">
                {(() => {
                  const order = orders.find(o => o.id === showRescheduleDialog);
                  return order ? (
                    <div className="service-info">
                      <h4>{order.serviceName}</h4>
                      <p><strong>Order ID:</strong> {order.orderId} | <strong>Current Date:</strong> {order.serviceDate}</p>
                    </div>
                  ) : null;
                })()}
                
                <div className="reschedule-inputs">
                  <div className="input-group">
                    <label htmlFor="reschedule-date">Select New Date:</label>
                    <input
                      id="reschedule-date"
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  
                  <div className="input-group">
                    <label>Select New Time:</label>
                    <div className="time-slots">
                      {generateTimeSlots().map((slot) => (
                        <button
                          key={slot.value}
                          className={`time-slot ${rescheduleTime === slot.value ? 'selected' : ''}`}
                          onClick={() => setRescheduleTime(slot.value)}
                          type="button"
                        >
                          {slot.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                
                {rescheduleError && (
                  <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    <span>{rescheduleError}</span>
                  </div>
                )}
                
                <div className="reschedule-note">
                  <p><strong>Important:</strong> Rescheduling request requires 72 hours prior to the original service date.</p>
                  <p>For changes within 72 hours, please contact customer support.</p>
                </div>
              </div>
            </div>
            <div className="dialog-actions">
              <button 
                className="dialog-button secondary"
                onClick={cancelRescheduleOrder}
                disabled={rescheduling}
              >
                Cancel
              </button>
              <button 
                className="dialog-button primary"
                onClick={() => {
                  const order = orders.find(o => o.id === showRescheduleDialog);
                  if (order) confirmRescheduleOrder(order);
                }}
                disabled={rescheduling}
              >
                {rescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && (
        <div className="cancel-dialog-overlay">
          <div className="cancel-dialog">
            <div className="dialog-header">
              <h3>Cancel Service Request</h3>
            </div>
            <div className="dialog-content">
              <p>Are you sure you want to cancel this service request?</p>
              <div className="cancel-order-details">
                {(() => {
                  const order = orders.find(o => o.id === showCancelDialog);
                  return order ? (
                    <>
                      <strong>{order.serviceName}</strong>
                      <br />
                      <span>Order ID: {order.orderId}</span>
                      <br />
                      <span>Service Date: {order.serviceDate}</span>
                    </>
                  ) : null;
                })()}
              </div>
              <p className="warning-text">
                <strong>Note:</strong> This action cannot be undone. Any payments made will be refunded according to our cancellation policy.
              </p>
            </div>
            <div className="dialog-actions">
              <button 
                className="dialog-button secondary"
                onClick={cancelCancelOrder}
              >
                Keep Order
              </button>
              <button 
                className="dialog-button danger"
                onClick={() => {
                  const order = orders.find(o => o.id === showCancelDialog);
                  if (order) confirmCancelOrder(order);
                }}
              >
                Yes, Cancel Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Cancel Confirmation Dialog */}
      {showGroupCancelDialog && (
        <div className="cancel-dialog-overlay">
          <div className="cancel-dialog">
            <div className="dialog-header">
              <h3>Cancel Group Booking</h3>
            </div>
            <div className="dialog-content">
              <p>Are you sure you want to cancel this entire group booking?</p>
              <div className="cancel-order-details">
                {(() => {
                  const groupOrders = showGroupCancelDialog ? groupedOrders[showGroupCancelDialog] : null;
                  return groupOrders ? (
                    <>
                      <strong>{groupOrders.length} services will be cancelled:</strong>
                      <ul>
                        {groupOrders.map((order: ExtendedBookingOrder) => (
                          <li key={order.id}>{order.serviceName} - {formatDate(order.serviceDate)}</li>
                        ))}
                      </ul>
                      <span>Group ID: {showGroupCancelDialog}</span>
                    </>
                  ) : null;
                })()}
              </div>
              <p className="warning-text">
                <strong>Note:</strong> This will cancel ALL services in this group booking. Any payments made will be refunded according to our cancellation policy.
              </p>
            </div>
            <div className="dialog-actions">
              <button 
                className="dialog-button secondary"
                onClick={cancelGroupCancelOrder}
              >
                Keep Group Booking
              </button>
              <button 
                className="dialog-button danger"
                onClick={() => {
                  if (showGroupCancelDialog) confirmCancelGroupBooking(showGroupCancelDialog);
                }}
              >
                Yes, Cancel All Services
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Information Dialog */}
      {refundInfo && (
        <div className="cancel-dialog-overlay">
          <div className="cancel-dialog">
            <div className="dialog-header">
              <h3>Refund Information</h3>
            </div>
            <div className="dialog-content">
              <div className="refund-details">
                <p><strong>
                  {refundInfo?.refund?.status === 'NOT_ELIGIBLE' ? 
                    'Service cancelled successfully!' : 
                    'Refund processed successfully!'}
                </strong></p>
                <div className="refund-info-details">
                  {refundInfo?.refund?.refundId && (
                    <span>Refund ID: {refundInfo.refund.refundId}</span>
                  )}
                  {refundInfo?.refund?.totalAmount && (
                    <span>Total Amount Paid: ₹{refundInfo.refund.totalAmount.toLocaleString()}</span>
                  )}
                  {refundInfo?.refund?.cancellationFee && refundInfo?.refund?.cancellationFee > 0 && (
                    <span>Cancellation Fee: ₹{refundInfo.refund.cancellationFee.toLocaleString()}</span>
                  )}
                  <span>Refund Amount: ₹{refundInfo?.refund?.amount?.toLocaleString() || '0'}</span>
                  <span>Status: {refundInfo?.refund?.refundStatus || refundInfo?.refund?.status}</span>
                  {refundInfo?.refund?.processedAt && (
                    <span>Processed: {formatDateTime(refundInfo.refund.processedAt)}</span>
                  )}
                </div>
                <p className="refund-note">
                  {refundInfo?.refund?.status === 'NOT_ELIGIBLE' ? 
                    'This booking is not eligible for a refund as per our cancellation policy.' :
                    'The refund will be credited to your original payment method within 5-7 business days.'}
                </p>
              </div>
            </div>
            <div className="dialog-actions">
              <button 
                className="dialog-button primary"
                onClick={() => setRefundInfo(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;